import {
  GetDuringAndPostExamSettingsFromExamCourseSettingsByExamCourseCodeDocument,
  GetExamCourseByExamCourseCodeDocument,
  type UpdateExamCourseSettingsMutationOptions,
  useGetExamCourseSettingsByExamCourseCodeLazyQuery,
  useUpdateExamCourseSettingsMutation,
} from '@exam-center/graphql';

import { type DuringAndPostExamSettingsSectionFormOutputs } from '../../containers/during-and-post-exam-settings-section-form/types';
import { useUploadExamCourseSettingsFileAttachment } from '../use-upload-exam-course-settings-file-attachment';

import {
  getAttachDocumentFormData,
  mapExamCourseSettingsFormInputsToExamCourseSettingsInput,
  mergeDuringAndPostExamSettingsInput,
} from './utils';

interface UseDuringAndPostExamSettingsFormSubmissionParams {
  examCourseId: string;
  examCourseCode: string;
  onCompleted?: UpdateExamCourseSettingsMutationOptions['onCompleted'];
  onError?: UpdateExamCourseSettingsMutationOptions['onError'];
}

export default function useDuringAndPostExamSettingsFormSubmission(
  params: UseDuringAndPostExamSettingsFormSubmissionParams,
) {
  const { examCourseId, examCourseCode, onCompleted, onError } = params;
  const [getExamCourseSettings, { loading: isGettingExamCourseSettings }] =
    useGetExamCourseSettingsByExamCourseCodeLazyQuery();
  const [updateExamCourseSettings, { loading: isSubmitting }] =
    useUpdateExamCourseSettingsMutation();

  const { handleUpload } = useUploadExamCourseSettingsFileAttachment({ examCourseId });

  const handleFormSubmit = async (data: DuringAndPostExamSettingsSectionFormOutputs) => {
    const uploadResult = await getAttachDocumentFormData(data.attachDocument, handleUpload);

    const { data: examCourseSettingsData } = await getExamCourseSettings({
      variables: {
        getExamCourseByExamCourseCodeInput: {
          examCourseCode,
        },
      },
      fetchPolicy: 'network-only',
    });

    const newSettingsInput = mapExamCourseSettingsFormInputsToExamCourseSettingsInput(
      data,
      uploadResult,
    );

    const mergedSettings = mergeDuringAndPostExamSettingsInput(
      examCourseSettingsData?.ecGetExamCourseByExamCourseCode.examCourse.settings,
      newSettingsInput,
    );

    await updateExamCourseSettings({
      variables: {
        updateExamCourseSettingsInput: {
          id: examCourseId,
          settings: mergedSettings,
        },
      },
      onCompleted,
      onError,
      refetchQueries: [
        GetExamCourseByExamCourseCodeDocument,
        GetDuringAndPostExamSettingsFromExamCourseSettingsByExamCourseCodeDocument,
      ],
      awaitRefetchQueries: true,
    });
  };

  return {
    handleFormSubmit,
    isSubmitting: isSubmitting || isGettingExamCourseSettings,
  };
}
